import React, { createContext, useContext, useState, useEffect } from 'react';
import { http } from '../config/http';

const MasterDataContext = createContext();

export const useMasterData = () => {
    return useContext(MasterDataContext);
};

export const MasterDataContextProvider = ({ children }) => {
    const [kategoriList, setKategoriList] = useState([]);
    const [cabangList, setCabangList] = useState([]);
    const [settings, setSettings] = useState(null);
    const [isLoadingMasterData, setIsLoadingMasterData] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    const fetchMasterData = async (overrideToken) => {
        // Only fetch if we have a token (user is authenticated)
        const token = overrideToken || localStorage.getItem('token');
        if (!token) {
            setKategoriList([]);
            setCabangList([]);
            setSettings(null);
            setIsLoaded(false);
            return;
        }

        setIsLoadingMasterData(true);
        try {
            const authHeader = `Bearer ${token}`;
            
            // Fire all requests in parallel
            const [resKategori, resCabang, resSettings] = await Promise.all([
                http.get('/kategori', { headers: { Authorization: authHeader } }).catch((err) => {
                    console.warn('Failed to load kategori master data:', err);
                    return { data: [] };
                }),
                http.get('/cabang', { headers: { Authorization: authHeader } }).catch((err) => {
                    console.warn('Failed to load cabang master data:', err);
                    return { data: { data: [] } };
                }),
                http.get('/settings', { headers: { Authorization: authHeader } }).catch((err) => {
                    console.warn('Failed to load settings master data:', err);
                    return { data: null };
                })
            ]);

            // Handle Kategori Response
            const katData = resKategori.data;
            if (Array.isArray(katData)) {
                setKategoriList(katData);
            } else if (katData && Array.isArray(katData.data)) {
                setKategoriList(katData.data);
            }

            // Handle Cabang Response
            const cabData = resCabang.data;
            if (Array.isArray(cabData)) {
                setCabangList(cabData);
            } else if (cabData && Array.isArray(cabData.data)) {
                setCabangList(cabData.data);
            }

            // Handle Settings Response
            const setData = resSettings.data;
            if (setData) {
                setSettings(setData.data || setData);
            }

            setIsLoaded(true);
        } catch (error) {
            console.error('Error fetching master data:', error);
        } finally {
            setIsLoadingMasterData(false);
        }
    };

    // Load data once when provider mounts
    useEffect(() => {
        fetchMasterData();
    }, []);

    // Listen for custom login/logout events & storage events for reactive updates
    useEffect(() => {
        const handleLoginSuccess = (e) => {
            const freshToken = e?.detail?.access_token || localStorage.getItem('token');
            fetchMasterData(freshToken);
        };
        const handleLogoutSuccess = () => {
            setKategoriList([]);
            setCabangList([]);
            setSettings(null);
            setIsLoaded(false);
        };
        const handleStorageChange = (e) => {
            if (e.key === 'token') {
                if (e.newValue) {
                    fetchMasterData(e.newValue);
                } else {
                    handleLogoutSuccess();
                }
            }
        };

        window.addEventListener('login-success', handleLoginSuccess);
        window.addEventListener('logout-success', handleLogoutSuccess);
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('login-success', handleLoginSuccess);
            window.removeEventListener('logout-success', handleLogoutSuccess);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // Derive Baki & Karat list once from settings / default
    const bakiList = React.useMemo(() => {
        if (settings && settings.baki_options) {
            const arr = String(settings.baki_options).split(',').map(item => item.trim()).filter(Boolean);
            const parsed = arr.map(Number).filter(n => !isNaN(n));
            if (parsed.length > 0) return parsed;
        }
        return Array.from({ length: 20 }, (_, i) => i + 1);
    }, [settings]);

    const karatList = React.useMemo(() => {
        if (settings && settings.karat_options) {
            const arr = String(settings.karat_options).split(',').map(item => item.trim()).filter(Boolean);
            const parsed = arr.map(Number).filter(n => !isNaN(n));
            if (parsed.length > 0) return parsed;
        }
        return [16, 17, 18, 19, 20, 21, 22, 23, 24];
    }, [settings]);

    const bankList = React.useMemo(() => {
        if (settings && settings.bank_options) {
            const arr = String(settings.bank_options).split(',').map(item => item.trim()).filter(Boolean);
            if (arr.length > 0) return arr;
        }
        return ['BCA', 'BNI', 'BRI', 'Mandiri', 'BSI', 'Lainnya'];
    }, [settings]);

    const qrisPayload = React.useMemo(() => {
        if (settings && settings.qris_payload) {
            return String(settings.qris_payload).trim();
        }
        const cached = localStorage.getItem('komala_qris_payload');
        if (cached) return cached.trim();
        return '';
    }, [settings]);

    const value = {
        kategoriList,
        cabangList,
        settings,
        bakiList,
        karatList,
        bankList,
        qrisPayload,
        isLoadingMasterData,
        isLoaded,
        refreshMasterData: fetchMasterData
    };

    return (
        <MasterDataContext.Provider value={value}>
            {children}
        </MasterDataContext.Provider>
    );
};
